import { useEffect, useState } from 'react';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../../components/ui/alert-dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Badge } from '../../components/ui/badge';
import api from '../../services/api';
import { toast } from 'sonner';
import { Clock, Plus, Calendar, Trash2, CheckCircle, XCircle, AlertCircle, ArrowUpDown, AlertTriangle, MessageSquare, Pencil } from 'lucide-react';
import { getCurrentDateString } from '../../utils/timezone';

const TimeEntries = () => {
  const [entries, setEntries] = useState([]);
  const [classes, setClasses] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [notesDialogOpen, setNotesDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [entryToDelete, setEntryToDelete] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [hoursUpdateNoteDialog, setHoursUpdateNoteDialog] = useState(false);
  const [selectedApprovedEntry, setSelectedApprovedEntry] = useState(null);
  const [hoursUpdateNote, setHoursUpdateNote] = useState('');
  const [businessTimezone, setBusinessTimezone] = useState('America/New_York');
  const [selectedMonth, setSelectedMonth] = useState(() => {
    // Use business timezone for initial month
    const dateStr = getCurrentDateString('America/New_York');
    return dateStr.substring(0, 7); // Get YYYY-MM
  });
  
  // Sorting state
  const [sortColumn, setSortColumn] = useState('date');
  const [sortDirection, setSortDirection] = useState('desc');
  
  // Duration mismatch state
  const [showMismatchReason, setShowMismatchReason] = useState(false);
  const [expectedHours, setExpectedHours] = useState(null);

  const [formData, setFormData] = useState({
    date: getCurrentDateString('America/New_York'),
    hours_worked: '',
    class_id: '',
    notes: '',
    duration_mismatch_reason: ''
  });
  
  // Coach notes form
  const [noteFormData, setNoteFormData] = useState({
    note_type: 'class',
    class_id: '',
    player_id: '',
    date: getCurrentDateString('America/New_York'),
    content: '',
    is_urgent: false
  });

  useEffect(() => {
    fetchTimezone();
    fetchData();
  }, [selectedMonth]);
  
  const fetchTimezone = async () => {
    try {
      const response = await api.get('/admin/timezone-settings');
      const tz = response.data.timezone || 'America/New_York';
      setBusinessTimezone(tz);
      
      // Update form dates to use business timezone
      const currentDate = getCurrentDateString(tz);
      setFormData(prev => ({ ...prev, date: currentDate }));
      setNoteFormData(prev => ({ ...prev, date: currentDate }));
    } catch (error) {
      // Use default timezone
      setBusinessTimezone('America/New_York');
    }
  };

  const fetchData = async () => {
    try {
      const [entriesRes, classesRes, statsRes] = await Promise.all([
        api.get(`/time-entries?month=${selectedMonth}`),
        api.get('/classes'),
        api.get('/dashboard/stats')
      ]);
      setEntries(entriesRes.data);
      setClasses(classesRes.data.filter(c => c.coach_id === statsRes.data.classes?.[0]?.coach_id || true));
      setStats(statsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Check for duration mismatch when class or hours change
  useEffect(() => {
    if (formData.class_id && formData.hours_worked) {
      const selectedClass = classes.find(c => c.id === formData.class_id);
      if (selectedClass && selectedClass.start_time && selectedClass.end_time) {
        const startParts = selectedClass.start_time.split(':');
        const endParts = selectedClass.end_time.split(':');
        const startMinutes = parseInt(startParts[0]) * 60 + parseInt(startParts[1]);
        const endMinutes = parseInt(endParts[0]) * 60 + parseInt(endParts[1]);
        const expected = (endMinutes - startMinutes) / 60;
        setExpectedHours(expected);
        
        if (Math.abs(parseFloat(formData.hours_worked) - expected) > 0.1) {
          setShowMismatchReason(true);
        } else {
          setShowMismatchReason(false);
          setFormData(prev => ({ ...prev, duration_mismatch_reason: '' }));
        }
      } else {
        setExpectedHours(null);
        setShowMismatchReason(false);
      }
    } else {
      setExpectedHours(null);
      setShowMismatchReason(false);
    }
  }, [formData.class_id, formData.hours_worked, classes]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.hours_worked || parseFloat(formData.hours_worked) <= 0) {
      toast.error('Please enter valid hours');
      return;
    }
    
    if (showMismatchReason && !formData.duration_mismatch_reason.trim()) {
      toast.error('Please provide a reason for the duration mismatch');
      return;
    }
    
    try {
      await api.post('/time-entries', {
        date: formData.date,
        hours_worked: parseFloat(formData.hours_worked),
        class_id: formData.class_id || null,
        notes: formData.notes || null,
        duration_mismatch_reason: formData.duration_mismatch_reason || null
      });
      toast.success('Time entry submitted');
      setDialogOpen(false);
      setFormData({
        date: getCurrentDateString(businessTimezone),
        hours_worked: '',
        class_id: '',
        notes: '',
        duration_mismatch_reason: ''
      });
      setShowMismatchReason(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit time entry');
    }
  };

  const openDeleteDialog = (entry) => {
    setEntryToDelete(entry);
    setDeleteDialogOpen(true);
  };

  const handleDelete = async () => {
    if (!entryToDelete) return;
    try {
      await api.delete(`/time-entries/${entryToDelete.id}`);
      toast.success('Entry deleted');
      setDeleteDialogOpen(false);
      setEntryToDelete(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete entry');
    }
  };

  const openEditDialog = (entry) => {
    setEditingEntry(entry);
    setFormData({
      date: entry.date,
      hours_worked: entry.hours_worked.toString(),
      class_id: entry.class_id || '',
      notes: entry.notes || '',
      duration_mismatch_reason: entry.duration_mismatch_reason || ''
    });
    setEditDialogOpen(true);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    if (!editingEntry) return;
    
    try {
      await api.put(`/time-entries/${editingEntry.id}`, {
        date: formData.date,
        hours_worked: parseFloat(formData.hours_worked),
        class_id: formData.class_id || null,
        notes: formData.notes,
        duration_mismatch_reason: formData.duration_mismatch_reason
      });
      toast.success('Entry updated');
      setEditDialogOpen(false);
      setEditingEntry(null);
      setFormData({
        date: getCurrentDateString(businessTimezone),
        hours_worked: '',
        class_id: '',
        notes: '',
        duration_mismatch_reason: ''
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update entry');
    }
  };

  const openHoursUpdateNoteDialog = (entry) => {
    setSelectedApprovedEntry(entry);
    setHoursUpdateNote('');
    setHoursUpdateNoteDialog(true);
  };

  const handleSendHoursUpdateNote = async (e) => {
    e.preventDefault();
    if (!hoursUpdateNote.trim()) {
      toast.error('Please enter the note with updated hours');
      return;
    }
    
    try {
      await api.post('/coach-notes', {
        note_type: 'hours_update',
        class_id: selectedApprovedEntry?.class_id || '',
        date: selectedApprovedEntry?.date || getCurrentDateString(businessTimezone),
        content: `Hours Update Request for entry on ${selectedApprovedEntry?.date}:\n${hoursUpdateNote}`,
        is_urgent: false,
        related_entry_id: selectedApprovedEntry?.id
      });
      toast.success('Hours update request sent to admin');
      setHoursUpdateNoteDialog(false);
      setSelectedApprovedEntry(null);
      setHoursUpdateNote('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send hours update request');
    }
  };

  const handleSubmitNote = async (e) => {
    e.preventDefault();
    if (!noteFormData.content.trim()) {
      toast.error('Please enter note content');
      return;
    }
    
    try {
      await api.post('/coach-notes', noteFormData);
      toast.success('Note sent to admin');
      setNotesDialogOpen(false);
      setNoteFormData({
        note_type: 'class',
        class_id: '',
        player_id: '',
        date: getCurrentDateString(businessTimezone),
        content: '',
        is_urgent: false
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send note');
    }
  };

  // Sorting
  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('desc');
    }
  };

  const sortedEntries = [...entries].sort((a, b) => {
    let aVal, bVal;
    switch (sortColumn) {
      case 'date':
        aVal = a.date;
        bVal = b.date;
        break;
      case 'hours':
        aVal = a.hours_worked;
        bVal = b.hours_worked;
        break;
      case 'class':
        aVal = a.class_name || '';
        bVal = b.class_name || '';
        break;
      case 'status':
        aVal = a.status;
        bVal = b.status;
        break;
      default:
        return 0;
    }
    if (sortDirection === 'asc') {
      return aVal < bVal ? -1 : aVal > bVal ? 1 : 0;
    } else {
      return aVal > bVal ? -1 : aVal < bVal ? 1 : 0;
    }
  });

  const getStatusBadge = (status) => {
    switch (status) {
      case 'approved':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
            <CheckCircle className="h-3 w-3" />
            Approved
          </span>
        );
      case 'rejected':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-rose-100 text-rose-700">
            <XCircle className="h-3 w-3" />
            Rejected
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-700">
            <AlertCircle className="h-3 w-3" />
            Pending
          </span>
        );
    }
  };

  // Generate month options
  const getMonthOptions = () => {
    const options = [];
    const now = new Date();
    for (let i = 0; i < 6; i++) {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const value = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      const label = date.toLocaleDateString('en-US', { year: 'numeric', month: 'long' });
      options.push({ value, label });
    }
    return options;
  };

  const totalHours = entries.reduce((sum, e) => sum + e.hours_worked, 0);
  const pendingHours = entries.filter(e => e.status === 'pending').reduce((sum, e) => sum + e.hours_worked, 0);
  const approvedHours = entries.filter(e => e.status === 'approved').reduce((sum, e) => sum + e.hours_worked, 0);

  const SortableHeader = ({ column, label }) => (
    <TableHead 
      className="cursor-pointer hover:bg-slate-50"
      onClick={() => handleSort(column)}
    >
      <div className="flex items-center gap-1">
        {label}
        <ArrowUpDown className={`h-3 w-3 ${sortColumn === column ? 'text-blue-500' : 'text-slate-400'}`} />
      </div>
    </TableHead>
  );

  if (loading) {
    return (
      <DashboardLayout title="My Hours">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-500 border-t-transparent"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="My Hours">
      <div className="space-y-6" data-testid="time-entries-page">
        {/* Header with Add Button */}
        <div className="space-y-3">
          <div>
            <p className="text-slate-600">Track and submit your working hours for billing</p>
          </div>
          <div className="flex flex-wrap gap-2">
            {/* Notes to Admin Button */}
            <Dialog open={notesDialogOpen} onOpenChange={setNotesDialogOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" data-testid="add-note-btn">
                  <MessageSquare className="h-4 w-4 mr-2" />
                  Note to Admin
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-md">
                <DialogHeader>
                  <DialogTitle className="font-heading text-xl">Send Note to Admin</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmitNote} className="space-y-4 mt-4">
                  <div className="space-y-2">
                    <Label>Note Type *</Label>
                    <Select
                      value={noteFormData.note_type}
                      onValueChange={(value) => setNoteFormData({ ...noteFormData, note_type: value })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="class">About a Class</SelectItem>
                        <SelectItem value="trial_player">About a Trial Player</SelectItem>
                        <SelectItem value="general">General Note</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {noteFormData.note_type === 'class' && (
                    <div className="space-y-2">
                      <Label>Class</Label>
                      <Select
                        value={noteFormData.class_id || "none"}
                        onValueChange={(value) => setNoteFormData({ ...noteFormData, class_id: value === "none" ? "" : value })}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select class" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">Select a class</SelectItem>
                          {classes.map((c) => (
                            <SelectItem key={c.id} value={c.id}>
                              {c.name} - {c.schedule_day}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  <div className="space-y-2">
                    <Label>Date *</Label>
                    <Input
                      type="date"
                      value={noteFormData.date}
                      onChange={(e) => setNoteFormData({ ...noteFormData, date: e.target.value })}
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Note Content *</Label>
                    <Textarea
                      value={noteFormData.content}
                      onChange={(e) => setNoteFormData({ ...noteFormData, content: e.target.value })}
                      placeholder="Enter your note for the admin..."
                      rows={4}
                      required
                    />
                  </div>

                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="is_urgent"
                      checked={noteFormData.is_urgent}
                      onChange={(e) => setNoteFormData({ ...noteFormData, is_urgent: e.target.checked })}
                      className="rounded"
                    />
                    <Label htmlFor="is_urgent" className="text-sm cursor-pointer">
                      Mark as urgent
                    </Label>
                  </div>

                  <div className="flex justify-end gap-3 pt-4">
                    <Button type="button" variant="outline" onClick={() => setNotesDialogOpen(false)}>
                      Cancel
                    </Button>
                    <Button type="submit" className="bg-blue-500 hover:bg-blue-600">
                      Send Note
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>

            {/* Log Hours Button */}
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button className="bg-blue-500 hover:bg-blue-600" data-testid="add-hours-btn">
                  <Plus className="h-4 w-4 mr-2" />
                  Log Hours
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-md">
                <DialogHeader>
                  <DialogTitle className="font-heading text-xl">Log Working Hours</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                  <div className="space-y-2">
                    <Label>Date *</Label>
                    <Input
                      type="date"
                      value={formData.date}
                      onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                      max={new Date().toISOString().split('T')[0]}
                      required
                      data-testid="entry-date-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Class (Optional)</Label>
                    <Select
                      value={formData.class_id || "none"}
                      onValueChange={(value) => setFormData({ ...formData, class_id: value === "none" ? "" : value })}
                    >
                      <SelectTrigger data-testid="entry-class-select">
                        <SelectValue placeholder="Select class (optional)" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">No specific class</SelectItem>
                        {(stats?.classes || classes).map((c) => (
                          <SelectItem key={c.id} value={c.id}>
                            {c.name} - {c.schedule_day}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>Hours Worked *</Label>
                    <Input
                      type="number"
                      step="0.5"
                      min="0.5"
                      max="24"
                      value={formData.hours_worked}
                      onChange={(e) => setFormData({ ...formData, hours_worked: e.target.value })}
                      placeholder="e.g., 4.5"
                      required
                      data-testid="entry-hours-input"
                    />
                    {expectedHours && (
                      <p className="text-xs text-slate-500">
                        Expected class duration: {expectedHours} hours
                      </p>
                    )}
                  </div>

                  {/* Duration Mismatch Warning */}
                  {showMismatchReason && (
                    <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg space-y-2">
                      <div className="flex items-center gap-2 text-amber-700">
                        <AlertTriangle className="h-4 w-4" />
                        <span className="text-sm font-medium">Hours differ from class duration</span>
                      </div>
                      <div className="space-y-1">
                        <Label className="text-sm">Reason (Required) *</Label>
                        <Textarea
                          value={formData.duration_mismatch_reason}
                          onChange={(e) => setFormData({ ...formData, duration_mismatch_reason: e.target.value })}
                          placeholder="Explain why hours differ from expected (e.g., arrived late, stayed for extra practice, etc.)"
                          rows={2}
                          required
                          data-testid="mismatch-reason-input"
                        />
                      </div>
                    </div>
                  )}

                  <div className="space-y-2">
                    <Label>Notes (Optional)</Label>
                    <Textarea
                      value={formData.notes}
                      onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                      placeholder="What did you work on?"
                      rows={2}
                      data-testid="entry-notes-input"
                    />
                  </div>

                  <div className="flex justify-end gap-3 pt-4">
                    <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                      Cancel
                    </Button>
                    <Button type="submit" className="bg-blue-500 hover:bg-blue-600" data-testid="submit-entry-btn">
                      Submit
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-3 gap-2 lg:gap-4">
          <Card className="border border-slate-200">
            <CardContent className="p-3 lg:p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs lg:text-sm text-slate-500">Total Hours</p>
                  <p className="font-heading text-xl lg:text-2xl font-bold text-slate-900">{totalHours.toFixed(1)}</p>
                </div>
                <Clock className="h-6 w-6 lg:h-8 lg:w-8 text-blue-500 shrink-0" />
              </div>
            </CardContent>
          </Card>
          <Card className="border border-slate-200">
            <CardContent className="p-3 lg:p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs lg:text-sm text-slate-500">Pending</p>
                  <p className="font-heading text-xl lg:text-2xl font-bold text-amber-600">{pendingHours.toFixed(1)}</p>
                </div>
                <AlertCircle className="h-6 w-6 lg:h-8 lg:w-8 text-amber-500 shrink-0" />
              </div>
            </CardContent>
          </Card>
          <Card className="border border-slate-200">
            <CardContent className="p-3 lg:p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs lg:text-sm text-slate-500">Approved</p>
                  <p className="font-heading text-xl lg:text-2xl font-bold text-green-600">{approvedHours.toFixed(1)}</p>
                </div>
                <CheckCircle className="h-6 w-6 lg:h-8 lg:w-8 text-green-500 shrink-0" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Month Filter */}
        <Card className="border border-slate-200">
          <CardHeader className="border-b border-slate-100 pb-4">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
              <CardTitle className="font-heading text-lg flex items-center gap-2">
                <Calendar className="h-5 w-5 text-blue-500" />
                Time Entries
              </CardTitle>
              <Select value={selectedMonth} onValueChange={setSelectedMonth}>
                <SelectTrigger className="w-48" data-testid="month-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {getMonthOptions().map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            {sortedEntries.length === 0 ? (
              <div className="p-12 text-center">
                <Clock className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500">No time entries for this month</p>
                <p className="text-sm text-slate-400 mt-1">Click "Log Hours" to add your first entry</p>
              </div>
            ) : (
              <>
              {/* Desktop Table */}
              <div className="hidden lg:block overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <SortableHeader column="date" label="Date" />
                      <SortableHeader column="hours" label="Hours" />
                      <SortableHeader column="class" label="Class" />
                      <TableHead>Notes</TableHead>
                      <SortableHeader column="status" label="Status" />
                      <TableHead className="w-12"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {sortedEntries.map((entry) => (
                      <TableRow key={entry.id} data-testid={`entry-${entry.id}`}>
                        <TableCell className="font-medium">
                          {(() => {
                            const [year, month, day] = entry.date.split('-').map(Number);
                            const date = new Date(year, month - 1, day);
                            return date.toLocaleDateString('en-US', {
                              weekday: 'short',
                              month: 'short',
                              day: 'numeric'
                            });
                          })()}
                        </TableCell>
                        <TableCell>
                          <div>
                            <span className="font-semibold">{entry.hours_worked}</span> hrs
                            {entry.expected_hours && Math.abs(entry.hours_worked - entry.expected_hours) > 0.1 && (
                              <Badge variant="outline" className="ml-2 text-xs text-amber-600 border-amber-300">
                                Expected: {entry.expected_hours}h
                              </Badge>
                            )}
                          </div>
                          {entry.duration_mismatch_reason && (
                            <p className="text-xs text-amber-600 mt-1">
                              Reason: {entry.duration_mismatch_reason}
                            </p>
                          )}
                        </TableCell>
                        <TableCell className="text-slate-600">
                          {entry.class_name || '-'}
                        </TableCell>
                        <TableCell className="max-w-xs truncate text-slate-600">
                          {entry.notes || '-'}
                        </TableCell>
                        <TableCell>{getStatusBadge(entry.status)}</TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            {entry.status === 'pending' && (
                              <>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => openEditDialog(entry)}
                                  className="text-blue-500 hover:text-blue-700 hover:bg-blue-50"
                                  data-testid={`edit-entry-${entry.id}`}
                                >
                                  <Pencil className="h-4 w-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => openDeleteDialog(entry)}
                                  className="text-rose-500 hover:text-rose-700 hover:bg-rose-50"
                                  data-testid={`delete-entry-${entry.id}`}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </>
                            )}
                            {entry.status === 'approved' && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => openHoursUpdateNoteDialog(entry)}
                                className="text-slate-500 hover:text-slate-700 hover:bg-slate-50"
                                title="Request hours update"
                                data-testid={`request-update-${entry.id}`}
                              >
                                <MessageSquare className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {/* Mobile Card View */}
              <div className="lg:hidden divide-y divide-slate-100">
                {sortedEntries.map((entry) => (
                  <div key={`m-${entry.id}`} className="p-3" data-testid={`entry-mobile-${entry.id}`}>
                    <div className="flex items-start justify-between mb-1.5">
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-slate-900 text-sm">
                          {(() => {
                            const [year, month, day] = entry.date.split('-').map(Number);
                            const date = new Date(year, month - 1, day);
                            return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
                          })()}
                        </p>
                        {entry.class_name && <p className="text-xs text-slate-500">{entry.class_name}</p>}
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        {getStatusBadge(entry.status)}
                      </div>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-bold text-slate-900">{entry.hours_worked} hrs</span>
                      <div className="flex gap-1">
                        {entry.status === 'pending' && (
                          <>
                            <Button variant="ghost" size="sm" onClick={() => openEditDialog(entry)} className="h-7 w-7 p-0 text-blue-500">
                              <Pencil className="h-3.5 w-3.5" />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => openDeleteDialog(entry)} className="h-7 w-7 p-0 text-rose-500">
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          </>
                        )}
                        {entry.status === 'approved' && (
                          <Button variant="ghost" size="sm" onClick={() => openHoursUpdateNoteDialog(entry)} className="h-7 w-7 p-0 text-slate-500">
                            <MessageSquare className="h-3.5 w-3.5" />
                          </Button>
                        )}
                      </div>
                    </div>
                    {entry.notes && <p className="text-xs text-slate-500 mt-1 truncate">{entry.notes}</p>}
                  </div>
                ))}
              </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Edit Entry Dialog */}
        <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl">Edit Time Entry</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleEditSubmit} className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label>Date *</Label>
                <Input
                  type="date"
                  value={formData.date}
                  onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label>Hours Worked *</Label>
                <Input
                  type="number"
                  step="0.5"
                  min="0.5"
                  max="24"
                  value={formData.hours_worked}
                  onChange={(e) => setFormData({ ...formData, hours_worked: e.target.value })}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label>Class (Optional)</Label>
                <Select
                  value={formData.class_id || "none"}
                  onValueChange={(value) => setFormData({ ...formData, class_id: value === "none" ? "" : value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="No class" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No specific class</SelectItem>
                    {classes.map((c) => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.name} - {c.schedule_day}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Notes (Optional)</Label>
                <Textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  rows={2}
                />
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setEditDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" className="bg-blue-500 hover:bg-blue-600">
                  Update
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Hours Update Note Dialog */}
        <Dialog open={hoursUpdateNoteDialog} onOpenChange={setHoursUpdateNoteDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl">Request Hours Update</DialogTitle>
              <DialogDescription>
                This entry has been approved. Send a note to admin requesting an update.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSendHoursUpdateNote} className="space-y-4 mt-4">
              <div className="bg-slate-50 p-3 rounded-lg">
                <p className="text-sm text-slate-600">
                  <strong>Date:</strong> {selectedApprovedEntry?.date}
                </p>
                <p className="text-sm text-slate-600">
                  <strong>Current Hours:</strong> {selectedApprovedEntry?.hours_worked} hrs
                </p>
              </div>
              
              <div className="space-y-2">
                <Label>Update Request *</Label>
                <Textarea
                  value={hoursUpdateNote}
                  onChange={(e) => setHoursUpdateNote(e.target.value)}
                  placeholder="Explain the hours correction needed (e.g., 'Actual hours should be 4.5 hours because I stayed extra for equipment setup')"
                  rows={3}
                  required
                />
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setHoursUpdateNoteDialog(false)}>
                  Cancel
                </Button>
                <Button type="submit" className="bg-amber-500 hover:bg-amber-600">
                  Send to Admin
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete Time Entry</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to delete this time entry for {entryToDelete?.date}? This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDelete}
                className="bg-rose-500 hover:bg-rose-600"
              >
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </DashboardLayout>
  );
};

export default TimeEntries;
